import { Field } from '@base-ui/react/field';
import type { ComponentProps, ReactNode } from 'react';
import { cn } from './cn';

/**
 * DESIGN.md › input: 48px, white, hairline border, body-md. Base UI Field wires
 * label/control/description. `indicator` is the small note at the label's right edge —
 * "(optional)" on the Figma field indicators — passed in translated. `error` replaces the hint
 * with the problem in red, next to `errorIcon`, and turns the border red.
 */
export function TextField({
  label,
  indicator,
  hint,
  error,
  errorIcon,
  className,
  ...props
}: ComponentProps<typeof Field.Control> & {
  label: string;
  indicator?: string;
  hint?: string;
  error?: string;
  errorIcon?: ReactNode;
  className?: string;
}) {
  return (
    <Field.Root
      invalid={error !== undefined}
      className={cn('flex flex-col gap-internal-md', className)}
    >
      <div className="flex items-center gap-internal-sm">
        <Field.Label className="font-brand text-label-sm text-on-surface">{label}</Field.Label>
        {indicator ? (
          <span className="flex-1 text-right font-brand text-body-sm text-on-surface-weak">
            {indicator}
          </span>
        ) : null}
      </div>
      <Field.Control
        className={cn(
          'h-tap-target rounded-sm border bg-surface px-internal-lg font-brand text-body-md text-on-surface placeholder:text-on-surface-weak focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-border-highlight',
          error !== undefined ? 'border-status-error' : 'border-border-default',
        )}
        {...props}
      />
      {error !== undefined ? (
        <p
          role="alert"
          className="flex items-center gap-internal-sm font-brand text-body-sm text-status-error"
        >
          {errorIcon}
          {error}
        </p>
      ) : hint ? (
        <Field.Description className="font-brand text-body-sm text-on-surface-weak">
          {hint}
        </Field.Description>
      ) : null}
    </Field.Root>
  );
}
